import React, { useState, useEffect } from 'react';
import { Headset, Search, X, CheckCircle, Clock } from 'lucide-react';
import api from '../../api';

const AdminServiceDesk = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [adminResponse, setAdminResponse] = useState('');
    const [updating, setUpdating] = useState(false);

    const fetchTickets = async () => {
        setLoading(true);
        try {
            const res = await api.get('/tickets/all');
            setTickets(res.data);
        } catch (err) {
            console.error("Failed to fetch tickets", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTickets();
    }, []);

    const handleUpdateTicket = async (status) => {
        setUpdating(true);
        try {
            const res = await api.put(`/tickets/${selectedTicket.id}`, {
                status: status,
                admin_response: adminResponse || selectedTicket.admin_response
            });
            
            setTickets(tickets.map(t => t.id === selectedTicket.id ? res.data : t));
            setSelectedTicket(null);
            setAdminResponse('');
        } catch (err) {
            console.error("Failed to update ticket", err);
            alert("Failed to update ticket. " + (err.response?.data?.detail || err.message));
        } finally {
            setUpdating(false);
        }
    };

    const filteredTickets = tickets.filter(t => 
        t.issue_type.toLowerCase().includes(searchQuery.toLowerCase()) || 
        t.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.user_id.toString().includes(searchQuery) ||
        (t.user_name && t.user_name.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    return (
        <div style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
            <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Headset size={24} color="var(--primary)" />
                        Service Desk (Admin)
                    </h2>
                    <p style={{ margin: '8px 0 0', color: 'var(--text-muted)' }}>Manage user support tickets and inquiries.</p>
                </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '0 16px', marginBottom: '24px' }}>
                <Search size={20} style={{ color: 'var(--text-muted)' }} />
                <input
                    type="text"
                    placeholder="Search tickets by issue, description, user name or ID..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    style={{ border: 'none', background: 'transparent', padding: '16px', outline: 'none', width: '100%', color: 'var(--text-main)' }}
                />
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>Loading tickets...</div>
            ) : filteredTickets.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>No tickets found.</div>
            ) : (
                <div style={{ background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--border)', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: 'var(--bg-card-hover)', borderBottom: '1px solid var(--border)', textAlign: 'left' }}>
                                <th style={{ padding: '16px', color: 'var(--text-muted)', fontWeight: 600 }}>Ticket ID</th>
                                <th style={{ padding: '16px', color: 'var(--text-muted)', fontWeight: 600 }}>User</th>
                                <th style={{ padding: '16px', color: 'var(--text-muted)', fontWeight: 600 }}>Issue Type</th>
                                <th style={{ padding: '16px', color: 'var(--text-muted)', fontWeight: 600 }}>Status</th>
                                <th style={{ padding: '16px', color: 'var(--text-muted)', fontWeight: 600 }}>Created At</th>
                                <th style={{ padding: '16px', color: 'var(--text-muted)', fontWeight: 600, textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTickets.map(ticket => (
                                <tr key={ticket.id} style={{ borderBottom: '1px solid var(--border)' }}>
                                    <td style={{ padding: '16px', color: 'var(--text-main)', fontWeight: 500 }}>#{ticket.id}</td>
                                    <td style={{ padding: '16px', color: 'var(--text-main)' }}>
                                        <div>{ticket.user_name}</div>
                                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>ID: {ticket.user_id}</div>
                                    </td>
                                    <td style={{ padding: '16px', color: 'var(--text-main)' }}>{ticket.issue_type}</td>
                                    <td style={{ padding: '16px' }}>
                                        <span style={{
                                            padding: '4px 8px', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600,
                                            background: ticket.status === 'OPEN' ? 'rgba(59, 130, 246, 0.1)' : ticket.status === 'RESOLVED' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                                            color: ticket.status === 'OPEN' ? '#3b82f6' : ticket.status === 'RESOLVED' ? '#10b981' : '#f59e0b'
                                        }}>
                                            {ticket.status}
                                        </span>
                                    </td>
                                    <td style={{ padding: '16px', color: 'var(--text-muted)' }}>{new Date(ticket.created_at).toLocaleDateString()}</td>
                                    <td style={{ padding: '16px', textAlign: 'right' }}>
                                        <button 
                                            onClick={() => {
                                                setSelectedTicket(ticket);
                                                setAdminResponse(ticket.admin_response || '');
                                            }}
                                            style={{ padding: '6px 12px', borderRadius: '6px', background: 'var(--primary)', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 500 }}
                                        >
                                            View & Respond
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Ticket Review Modal */}
            {selectedTicket && (
                <div className="modal-overlay" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 999 }}>
                    <div className="modal-box" style={{ background: 'var(--bg-main)', width: '100%', maxWidth: '600px', borderRadius: '12px', overflow: 'hidden' }}>
                        <div style={{ padding: '20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h2 style={{ margin: 0, color: 'var(--text-main)', fontSize: '1.25rem' }}>Ticket #{selectedTicket.id} - {selectedTicket.issue_type}</h2>
                            <button onClick={() => setSelectedTicket(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}><X size={24} /></button>
                        </div>
                        
                        <div style={{ padding: '20px', maxHeight: '70vh', overflowY: 'auto' }}>
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '4px' }}>USER</label>
                                <div style={{ color: 'var(--text-main)', fontSize: '1rem' }}>{selectedTicket.user_name} (ID: {selectedTicket.user_id})</div>
                            </div>
                            
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '4px' }}>USER DESCRIPTION</label>
                                <div style={{ background: 'var(--bg-card)', padding: '16px', borderRadius: '8px', border: '1px solid var(--border)', color: 'var(--text-main)', fontSize: '0.95rem', lineHeight: 1.5 }}>
                                    {selectedTicket.description}
                                </div>
                            </div>
                            
                            <div style={{ marginBottom: '24px' }}>
                                <label style={{ display: 'block', fontSize: '0.85rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '8px' }}>ADMIN RESPONSE</label>
                                <textarea 
                                    value={adminResponse}
                                    onChange={(e) => setAdminResponse(e.target.value)}
                                    placeholder="Type a response to the user..."
                                    style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-main)', minHeight: '120px', fontSize: '1rem', resize: 'vertical' }}
                                />
                            </div>
                        </div>

                        <div style={{ padding: '20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: '12px', background: 'var(--bg-card)' }}>
                            <button 
                                onClick={() => handleUpdateTicket('IN_PROGRESS')}
                                disabled={updating}
                                style={{ padding: '10px 16px', borderRadius: '8px', border: 'none', background: 'var(--surface-hover)', color: 'var(--text-main)', cursor: updating ? 'not-allowed' : 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                                <Clock size={18} /> Mark In Progress
                            </button>
                            <button 
                                onClick={() => handleUpdateTicket('RESOLVED')}
                                disabled={updating}
                                style={{ padding: '10px 16px', borderRadius: '8px', border: 'none', background: '#10b981', color: 'white', cursor: updating ? 'not-allowed' : 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}
                            >
                                <CheckCircle size={18} /> Mark Resolved
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminServiceDesk;
